import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "@/lib/toast";
import { Save, AlertTriangle, Video, Building2, Phone, Mail, MapPin, Hash, Package, Zap } from "lucide-react";

const FIELDS = [
  { key: "hero_video_url", label: "Hero video URL", icon: Video, type: "text",
    placeholder: "https://… (mp4 or YouTube)",
    hint: "Tip: a 6–15s drone install loop dramatically lifts hero conversion." },
  { key: "license_number", label: "License number", icon: Hash, type: "text" },
  { key: "phone", label: "Public phone", icon: Phone, type: "text" },
  { key: "email", label: "Public email", icon: Mail, type: "email" },
  { key: "address", label: "Office address", icon: MapPin, type: "text" },
];

export default function AdminSettings() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const r = await api.get("/admin/site-settings");
    setS(r.data);
  };
  useEffect(() => { load(); }, []);

  const set = (k, v) => setS((cur) => ({ ...cur, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/site-settings", s);
      toast.success("Settings saved");
      load();
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (!s) return <div className="text-[#475569]">Loading…</div>;

  return (
    <div data-testid="admin-settings" className="max-w-3xl">
      <p className="overline">Site settings</p>
      <h1 className="text-3xl font-heading font-medium text-[#0A2540] dark:text-white mt-2 mb-2">Brand & site</h1>
      <p className="text-sm text-[#475569] dark:text-slate-400 mb-8 max-w-xl">
        Tune what visitors see on the public site. Changes go live immediately.
      </p>

      {/* Hero video toggle */}
      <div className="card-pb p-6 mb-6" data-testid="settings-hero-video">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="font-heading text-lg text-[#0A2540] dark:text-white flex items-center gap-2">
              <Video className="w-4 h-4 text-[#C9A356]" /> Hero video
            </h2>
            <p className="text-sm text-[#475569] dark:text-slate-400 mt-1">
              When enabled, the landing hero plays your install b-roll instead of the static photo.
            </p>
          </div>
          <Toggle
            checked={!!s.hero_video_enabled}
            onChange={(v) => set("hero_video_enabled", v)}
            testId="toggle-hero-video"
          />
        </div>
      </div>

      {/* Hurricane mode */}
      <div className="card-pb p-6 mb-6 border-l-4 border-amber-500" data-testid="settings-hurricane">
        <div className="flex items-start justify-between gap-4 mb-3">
          <div>
            <h2 className="font-heading text-lg text-[#0A2540] dark:text-white flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-500" /> Hurricane / storm mode
            </h2>
            <p className="text-sm text-[#475569] dark:text-slate-400 mt-1">
              Shows a sticky amber banner on the landing page and every dashboard until you turn it off.
            </p>
          </div>
          <Toggle
            checked={!!s.hurricane_mode_enabled}
            onChange={(v) => set("hurricane_mode_enabled", v)}
            testId="toggle-hurricane"
          />
        </div>
        <textarea
          value={s.hurricane_message || ""}
          onChange={(e) => set("hurricane_message", e.target.value)}
          rows={3}
          maxLength={300}
          placeholder="Storm watch message shown to all visitors and clients…"
          data-testid="hurricane-message"
          className="w-full border border-[#E2E8F0] dark:border-slate-700 dark:bg-slate-800 dark:text-white rounded-md px-3 py-2 text-sm"
        />
      </div>

      {/* Brand fields */}
      <div className="card-pb p-6 mb-6">
        <h2 className="font-heading text-lg text-[#0A2540] dark:text-white mb-4 flex items-center gap-2">
          <Building2 className="w-4 h-4 text-[#C9A356]" /> Public brand info
        </h2>
        <div className="grid sm:grid-cols-2 gap-4">
          {FIELDS.map((f) => (
            <div key={f.key} data-testid={`field-${f.key}`} className={f.key === "address" ? "sm:col-span-2" : ""}>
              <label className="block text-xs uppercase tracking-wider text-[#475569] dark:text-slate-400 mb-1.5 font-bold flex items-center gap-1.5">
                <f.icon className="w-3 h-3" /> {f.label}
              </label>
              <input
                type={f.type}
                value={s[f.key] || ""}
                onChange={(e) => set(f.key, e.target.value)}
                placeholder={f.placeholder}
                data-testid={`input-${f.key}`}
                className="w-full border border-[#E2E8F0] dark:border-slate-700 dark:bg-slate-800 dark:text-white rounded-md px-3 py-2 text-sm"
              />
              {f.hint && <p className="text-[10px] text-[#94A3B8] mt-1">{f.hint}</p>}
            </div>
          ))}
        </div>
      </div>

      {/* Kit configuration */}
      <div className="card-pb p-6 mb-6" data-testid="settings-kit-config">
        <h2 className="font-heading text-lg text-[#0A2540] dark:text-white mb-1 flex items-center gap-2">
          <Package className="w-4 h-4 text-[#C9A356]" /> Kit auto-reserve ratios
        </h2>
        <p className="text-sm text-[#475569] dark:text-slate-400 mb-4">
          When a solar project is created with a kW target, PowerBuilt automatically reserves this standard kit from inventory.
          Tune the ratios so new hardware gets picked correctly.
        </p>

        {["solar_install", "solar_upgrade"].map((pt) => {
          const cfg = s.kit_config?.[pt] || {
            panel_watts: 400, inverter_kw: 5, mounting_panels_per_kit: 4, wiring_panels_per_run: 8,
          };
          const setCfg = (key, val) => set("kit_config", {
            ...(s.kit_config || {}),
            [pt]: { ...cfg, [key]: val === "" ? "" : Number(val) },
          });
          return (
            <div key={pt} className="mb-5 last:mb-0 rounded-lg border border-[#E2E8F0] dark:border-slate-700 p-4">
              <div className="flex items-center gap-2 mb-3">
                <Zap className="w-3.5 h-3.5 text-[#C9A356]" />
                <span className="font-semibold text-sm text-[#0A2540] dark:text-white capitalize">
                  {pt.replace("_", " ")}
                </span>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <NumberField
                  label="Panel wattage (W)"
                  hint="Panels = ceil(kW × 1000 / this value)"
                  value={cfg.panel_watts}
                  onChange={(v) => setCfg("panel_watts", v)}
                  testid={`kit-${pt}-panel-w`}
                />
                <NumberField
                  label="Inverter class (kW)"
                  hint="Inverters = ceil(kW / this value)"
                  value={cfg.inverter_kw}
                  onChange={(v) => setCfg("inverter_kw", v)}
                  testid={`kit-${pt}-inverter-kw`}
                />
                <NumberField
                  label="Mounting kit ratio"
                  hint="1 kit per N panels"
                  value={cfg.mounting_panels_per_kit}
                  onChange={(v) => setCfg("mounting_panels_per_kit", v)}
                  testid={`kit-${pt}-mounting`}
                />
                <NumberField
                  label="Wiring run ratio"
                  hint="1 run per N panels"
                  value={cfg.wiring_panels_per_run}
                  onChange={(v) => setCfg("wiring_panels_per_run", v)}
                  testid={`kit-${pt}-wiring`}
                />
              </div>
              {/* Live preview */}
              {cfg.panel_watts && cfg.inverter_kw && (
                <p className="mt-3 text-[11px] text-[#475569] dark:text-slate-400">
                  <strong>5 kW example:</strong>{" "}
                  {Math.ceil((5 * 1000) / cfg.panel_watts)} panels ·{" "}
                  {Math.ceil(5 / cfg.inverter_kw)} inverter(s) ·{" "}
                  {Math.ceil(Math.ceil((5 * 1000) / cfg.panel_watts) / cfg.mounting_panels_per_kit)} mounting kit(s) ·{" "}
                  {Math.ceil(Math.ceil((5 * 1000) / cfg.panel_watts) / cfg.wiring_panels_per_run)} wiring run(s)
                </p>
              )}
            </div>
          );
        })}
      </div>

      <button onClick={save} disabled={saving} className="btn-primary" data-testid="save-settings">
        <Save className="w-4 h-4 mr-2" /> {saving ? "Saving…" : "Save settings"}
      </button>
    </div>
  );
}

function NumberField({ label, hint, value, onChange, testid }) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-wider text-[#475569] dark:text-slate-400 mb-1 font-bold">{label}</label>
      <input
        type="number" min="1" step="0.1"
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full border border-[#E2E8F0] dark:border-slate-700 dark:bg-slate-800 dark:text-white rounded-md px-3 h-10 text-sm tabular-nums"
      />
      {hint && <p className="text-[10px] text-[#94A3B8] mt-1">{hint}</p>}
    </div>
  );
}

function Toggle({ checked, onChange, testId }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      data-testid={testId}
      role="switch"
      aria-checked={checked}
      className={`relative inline-flex h-6 w-11 shrink-0 rounded-full transition-colors ${
        checked ? "bg-emerald-500" : "bg-slate-300 dark:bg-slate-600"
      }`}
    >
      <span
        className={`inline-block h-5 w-5 rounded-full bg-white shadow transform transition-transform ${
          checked ? "translate-x-5" : "translate-x-0.5"
        } translate-y-0.5`}
      />
    </button>
  );
}
